/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeDeleteRequest, makePostRequest } from "../../network/http-client"

import type { UserTotpStore } from "./user-totp-store"
import type { ConfirmTotp, DisableTotp, TotpCode } from "./user-types"

enum Endpoint {
    totp = "/api/private/user/me/totp",
    user = "/api/private/user"
}

export class UserTotpService {
    constructor(
        private readonly store: UserTotpStore,
        private readonly logger: LoggerStore
    ) {}

    async generateTotp(): Promise<Result<TotpCode>> {
        // Clear the previous QR code before requesting a new secret. If the
        // request fails, the old secret must not remain visible.
        this.store.clearTotpCode()

        const res = await makePostRequest<void, TotpCode>(`${Endpoint.totp}/setup`, {}, undefined, "blob")

        if (res.success && res.data) {
            this.store.setTotpCode(res.data)
        }
        if (!res.success) {
            this.logger.logApiError("Generate TOTP failed", res.apiError, res.error)
        }
        return res
    }

    async disableTotp(code: string): Promise<Result<void>> {
        const res = await makePostRequest<DisableTotp, void>(`${Endpoint.totp}/disable`, {}, { code })

        if (res.success) {
            this.logger.logOk("TOTP disabled successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Disable TOTP failed", res.apiError, res.error)
        }
        return res
    }

    async confirmTotp(code: string): Promise<Result<void>> {
        const res = await makePostRequest<ConfirmTotp, void>(`${Endpoint.totp}/confirm`, {}, { code })

        if (res.success) {
            this.logger.logOk("TOTP confirmed successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Confirm TOTP failed", res.apiError, res.error)
        }
        return res
    }

    async resetTotp(userId: number): Promise<Result<void>> {
        const res = await makeDeleteRequest<void>(`${Endpoint.user}/${userId}/totp`, {})
        if (res.success) {
            this.logger.logOk("User TOTP reset successfully")
        }
        if (!res.success) {
            this.logger.logApiError("TOTP reset failed", res.apiError, res.error)
        }

        return res
    }
}
